import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import './AdminPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [emailSettings, setEmailSettings] = useState({});
  const [logs, setLogs] = useState([]);
  const [sourceHealth, setSourceHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [scrapeRunning, setScrapeRunning] = useState(false);
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSource, setNewSource] = useState({ source: '', label: '', feed_url: '', keywords: '' });
  const { token } = useAuthStore();

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const response = await axios.get(`${API_BASE}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data);
      } else if (activeTab === 'updates') {
        const response = await axios.get(`${API_BASE}/admin/updates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUpdates(response.data);
      } else if (activeTab === 'email') {
        const response = await axios.get(`${API_BASE}/admin/email-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmailSettings(response.data || {});
      } else if (activeTab === 'logs') {
        const response = await axios.get(`${API_BASE}/admin/logs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLogs(response.data);
      } else if (activeTab === 'sources') {
        const response = await axios.get(`${API_BASE}/admin/scraper-health`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSourceHealth(response.data.sources || []);
      }
    } catch (error) {
      toast.error(`Error loading ${activeTab}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetUserPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`${API_BASE}/admin/users/${userId}/reset-password`, 
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password reset successfully');
      setResetPasswordId(null);
      setNewPassword('');
      loadData();
    } catch (error) {
      toast.error('Error resetting password');
    }
  };

  const toggleUserActive = async (userId) => {
    try {
      await axios.put(`${API_BASE}/admin/users/${userId}/toggle-active`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User status updated');
      loadData();
    } catch (error) {
      toast.error('Error updating user status');
    }
  };

  const toggleUserAdmin = async (userId) => {
    try {
      await axios.put(`${API_BASE}/admin/users/${userId}/toggle-admin`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Admin status updated');
      loadData();
    } catch (error) {
      toast.error('Error updating admin status');
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${userEmail} deleted successfully`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting user');
    }
  };

  const triggerScrapeNow = async () => {
    setScrapeRunning(true);
    try {
      await axios.post(`${API_BASE}/admin/scrape-now`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scrape started - refresh in a minute to see updated timestamps');
      setTimeout(loadData, 15000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error starting scrape');
    } finally {
      setTimeout(() => setScrapeRunning(false), 5000);
    }
  };

  const toggleSourceEnabled = async (src) => {
    try {
      await axios.put(`${API_BASE}/admin/sources/${src.id}`, { enabled: !src.enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${src.label} ${src.enabled ? 'disabled' : 'enabled'}`);
      loadData();
    } catch (error) {
      toast.error('Error updating source');
    }
  };

  const startEditSource = (src) => {
    setEditingSourceId(src.id);
    setEditForm({ label: src.label, feed_url: src.feed_url || '', keywords: src.keywords || '' });
  };

  const saveEditSource = async (id) => {
    try {
      await axios.put(`${API_BASE}/admin/sources/${id}`, editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Source updated');
      setEditingSourceId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error updating source');
    }
  };

  const deleteSource = async (src) => {
    if (!window.confirm(`Delete source "${src.label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/sources/${src.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Source deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting source');
    }
  };

  const addSource = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/admin/sources`, newSource,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Source added - it will be included in the next scrape');
      setNewSource({ source: '', label: '', feed_url: '', keywords: '' });
      setShowAddSource(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error adding source');
    }
  };

  const saveEmailSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_BASE}/admin/email-settings`, emailSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email settings saved');
      loadData();
    } catch (error) {
      toast.error('Error saving email settings');
    }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-container">
      <h1>Admin Panel</h1>

      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users Management
        </button>
        <button 
          className={`tab ${activeTab === 'updates' ? 'active' : ''}`}
          onClick={() => setActiveTab('updates')}
        >
          ISP Updates
        </button>
        <button 
          className={`tab ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          Email Settings
        </button>
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Audit Logs
        </button>
        <button
          className={`tab ${activeTab === 'sources' ? 'active' : ''}`}
          onClick={() => setActiveTab('sources')}
        >
          Data Sources
        </button>
      </div>

      {/* Users Management Tab */}
      {activeTab === 'users' && (
        <div className="tab-content">
          <h2>User Management</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Full Name</th>
                <th>Admin</th>
                <th>Active</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.full_name || '-'}</td>
                  <td>
                    <span className={`badge ${user.is_admin ? 'badge-success' : 'badge-gray'}`}>
                      {user.is_admin ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="actions">
                    {resetPasswordId === user.id ? (
                      <div className="password-form">
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="New password"
                        />
                        <button onClick={() => resetUserPassword(user.id)} className="btn-success">Set</button>
                        <button onClick={() => setResetPasswordId(null)} className="btn-cancel">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <button onClick={() => setResetPasswordId(user.id)} className="btn-reset">Reset PWD</button>
                        <button onClick={() => toggleUserActive(user.id)} className="btn-toggle">
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => toggleUserAdmin(user.id)} className="btn-admin">
                          {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                        </button>
                        <button onClick={() => deleteUser(user.id, user.email)} className="btn-delete">Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ISP Updates Tab */}
      {activeTab === 'updates' && (
        <div className="tab-content">
          <h2>ISP Updates</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Title</th>
                <th>Classification</th>
                <th>Urgency</th>
                <th>Duplicate</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {updates.map(update => (
                <tr key={update.id}>
                  <td>{update.source}</td>
                  <td>{update.title}</td>
                  <td>{update.classified_as || '-'}</td>
                  <td>
                    <span className={`urgency-${update.urgency}`}>
                      {update.urgency || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${update.is_duplicate ? 'badge-danger' : 'badge-success'}`}>
                      {update.is_duplicate ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{new Date(update.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {updates.length === 0 && <p className="empty-message">No updates found</p>}
        </div>
      )}

      {/* Email Settings Tab */}
      {activeTab === 'email' && (
        <div className="tab-content">
          <h2>Email Settings</h2>
          <form onSubmit={saveEmailSettings} className="email-form">
            <div className="form-group">
              <label>SMTP Host</label>
              <input
                type="text"
                value={emailSettings.smtp_host || ''}
                onChange={(e) => setEmailSettings({...emailSettings, smtp_host: e.target.value})}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div className="form-group">
              <label>SMTP Port</label>
              <input
                type="number"
                value={emailSettings.smtp_port || ''}
                onChange={(e) => setEmailSettings({...emailSettings, smtp_port: e.target.value})}
                placeholder="587"
              />
            </div>
            <div className="form-group">
              <label>SMTP Username</label>
              <input
                type="email"
                value={emailSettings.smtp_user || ''}
                onChange={(e) => setEmailSettings({...emailSettings, smtp_user: e.target.value})}
                placeholder="your@email.com"
              />
            </div>
            <div className="form-group">
              <label>SMTP Password</label>
              <input
                type="password"
                onChange={(e) => setEmailSettings({...emailSettings, smtp_password: e.target.value})}
                placeholder="Leave blank to keep current"
              />
            </div>
            <div className="form-group">
              <label>From Email</label>
              <input
                type="email"
                value={emailSettings.from_email || ''}
                onChange={(e) => setEmailSettings({...emailSettings, from_email: e.target.value})}
                placeholder="noreply@news.iva.am"
              />
            </div>
            <div className="form-group">
              <label>From Name</label>
              <input
                type="text"
                value={emailSettings.from_name || ''}
                onChange={(e) => setEmailSettings({...emailSettings, from_name: e.target.value})}
                placeholder="ISP Policy Monitor"
              />
            </div>
            <button type="submit" className="btn-save">Save Email Settings</button>
          </form>
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'logs' && (
        <div className="tab-content">
          <h2>Audit Logs</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Admin</th>
                <th>Action</th>
                <th>Target</th>
                <th>Timestamp</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td>{log.admin_email || 'Unknown'}</td>
                  <td>{log.action}</td>
                  <td>{log.target_type} #{log.target_id}</td>
                  <td>{new Date(log.created_at).toLocaleString()}</td>
                  <td className="details">{log.details ? JSON.parse(log.details).email || '' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && <p className="empty-message">No logs found</p>}
        </div>
      )}

      {/* Data Sources / Scraper Health Tab */}
      {activeTab === 'sources' && (
        <div className="tab-content">
          <div className="sources-header">
            <h2>Data Sources</h2>
            <div className="sources-header-actions">
              <button className="btn-save" onClick={triggerScrapeNow} disabled={scrapeRunning}>
                {scrapeRunning ? 'Scraping...' : '⟳ Scrape Now'}
              </button>
              <button className="btn-toggle" onClick={() => setShowAddSource(!showAddSource)}>
                {showAddSource ? 'Cancel' : '+ Add RSS Source'}
              </button>
            </div>
          </div>

          {showAddSource && (
            <form onSubmit={addSource} className="email-form source-add-form">
              <div className="form-group">
                <label>Source Key (lowercase, no spaces)</label>
                <input
                  type="text"
                  value={newSource.source}
                  onChange={(e) => setNewSource({ ...newSource, source: e.target.value })}
                  placeholder="e.g. m3aawg"
                  required
                />
              </div>
              <div className="form-group">
                <label>Display Label</label>
                <input
                  type="text"
                  value={newSource.label}
                  onChange={(e) => setNewSource({ ...newSource, label: e.target.value })}
                  placeholder="e.g. M3AAWG Blog"
                  required
                />
              </div>
              <div className="form-group">
                <label>Feed URL (RSS/Atom)</label>
                <input
                  type="url"
                  value={newSource.feed_url}
                  onChange={(e) => setNewSource({ ...newSource, feed_url: e.target.value })}
                  placeholder="https://example.com/feed/"
                  required
                />
              </div>
              <div className="form-group">
                <label>Keywords (comma-separated, optional - leave blank to accept all items)</label>
                <input
                  type="text"
                  value={newSource.keywords}
                  onChange={(e) => setNewSource({ ...newSource, keywords: e.target.value })}
                  placeholder="dmarc, spf, dkim, deliverability"
                />
              </div>
              <button type="submit" className="btn-save">Add Source</button>
            </form>
          )}

          <table className="admin-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Type</th>
                <th>Status</th>
                <th>Total Items</th>
                <th>Last 7 Days</th>
                <th>Last Scraped</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sourceHealth.map(src => (
                <React.Fragment key={src.source}>
                  <tr>
                    <td>{src.label}</td>
                    <td>{src.type === 'rss' ? 'RSS' : 'Custom'}</td>
                    <td>
                      {src.status === 'healthy' && <span className="badge badge-success">Healthy</span>}
                      {src.status === 'stale' && <span className="badge badge-warning">Stale</span>}
                      {src.status === 'dead' && <span className="badge badge-danger">Dead</span>}
                      {src.status === 'disabled' && <span className="badge badge-gray">Disabled</span>}
                    </td>
                    <td>{src.total_count || 0}</td>
                    <td>{src.last_7d ?? '-'}</td>
                    <td>{src.last_scraped_at ? new Date(src.last_scraped_at).toLocaleString() : 'Never'}</td>
                    <td className="actions">
                      <button onClick={() => toggleSourceEnabled(src)} className="btn-toggle">
                        {src.enabled ? 'Disable' : 'Enable'}
                      </button>
                      {src.type === 'rss' && (
                        <button onClick={() => startEditSource(src)} className="btn-reset">Edit</button>
                      )}
                      {!src.is_builtin && (
                        <button onClick={() => deleteSource(src)} className="btn-delete">Delete</button>
                      )}
                    </td>
                  </tr>
                  {editingSourceId === src.id && (
                    <tr>
                      <td colSpan="7">
                        <div className="source-edit-form">
                          <div className="form-group">
                            <label>Label</label>
                            <input
                              type="text"
                              value={editForm.label}
                              onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Feed URL</label>
                            <input
                              type="url"
                              value={editForm.feed_url}
                              onChange={(e) => setEditForm({ ...editForm, feed_url: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Keywords (comma-separated)</label>
                            <input
                              type="text"
                              value={editForm.keywords}
                              onChange={(e) => setEditForm({ ...editForm, keywords: e.target.value })}
                            />
                          </div>
                          <button onClick={() => saveEditSource(src.id)} className="btn-success">Save</button>
                          <button onClick={() => setEditingSourceId(null)} className="btn-cancel">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
